import round from 'lodash/round';
import { CSSProperties } from 'react';
import styled from 'styled-components';

import { ButtonSelect } from '~src/components/ButtonSelect';
import { Divider } from '~src/components/Divider';
import { Heading, Text } from '~src/components/Typography';
import { useAnalytics } from '~src/tracking/ga';
import { formatCurrency, getBooleanFromValue } from '~src/util';

type SavingsEstimateProps = {
  style?: CSSProperties;
  paybackTime: string;
  showLoan?: boolean;
  totalPrice?: number;
  savedAnnually?: number;
  co2SavedAnnually?: number;
  financingMonthlyPayment?: number;
  financingTotalPaid?: number;
  setShowLoan?: (showLoan: boolean) => void;
};

export const SavingsEstimateBase = ({
  style,
  showLoan,
  setShowLoan,
  paybackTime,
  totalPrice = 0,
  savedAnnually = 0,
  financingMonthlyPayment = 0,
  financingTotalPaid = 0,
  co2SavedAnnually = 0,
}: SavingsEstimateProps) => {
  const { trackEvent } = useAnalytics();

  const savings15Years = savedAnnually * 15 - (showLoan ? financingTotalPaid : totalPrice);

  return (
    <div style={style}>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Heading color='light' level={3}>
          Beregninger
        </Heading>

        {setShowLoan && (
          <ButtonSelect
            label='Vis lån'
            onChange={value => {
              const valueAsBoolean = getBooleanFromValue(value);

              setShowLoan(valueAsBoolean);
              trackEvent('click_financing_toggle', { financing_enabled: valueAsBoolean });
            }}
            value={`${showLoan}`}
            options={[
              { value: 'false', label: 'Kontant' },
              { value: 'true', label: `Lån${showLoan ? '*' : ''}` },
            ]}
            style={{ height: '28px' }}
            itemWidth={70}
          />
        )}
      </div>

      <Divider style={{ margin: '10px 0 20px 0' }} />

      <div>
        <SavingsEstimateEntry label='Pris' value={formatCurrency(showLoan ? 0 : totalPrice, { decimals: 0 })} />

        {showLoan && financingMonthlyPayment && (
          <SavingsEstimateEntry
            label='Årlig ydelse'
            value={formatCurrency(financingMonthlyPayment * 12, { decimals: 0 })}
          />
        )}

        {savings15Years > 0 && (
          <SavingsEstimateEntry label='Gevinst efter 15 år' value={formatCurrency(savings15Years, { decimals: 0 })} />
        )}

        <SavingsEstimateEntry label='Årlig CO2 besparelse' value={`${round(co2SavedAnnually)} kg`} />

        <SavingsEstimateEntry label='Tilbagebetalingstid' value={paybackTime} />
      </div>
    </div>
  );
};

const EntryWrapper = styled.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '16px',
});

type SavingsEstimateEntryEntryProps = {
  label: string;
  value?: string;
};

const SavingsEstimateEntry = ({ label, value }: SavingsEstimateEntryEntryProps) => {
  return (
    <EntryWrapper>
      <Text style={{ lineHeight: '20px' }} color='light'>
        {label}
      </Text>

      <Heading style={{ lineHeight: '20px' }} color='light'>
        {value ?? '-'}
      </Heading>
    </EntryWrapper>
  );
};
